import { Accordion, Box, Button, Span, Text, Flex, IconButton, AccordionValueChangeDetails, SkeletonText, Stack } from "@chakra-ui/react"// Added Flex, Text, IconButton, SkeletonText, Stack
import { useState } from "react";
// Using icons directly in the trigger might be better if Chakra's Accordion doesn't handle internal rotation well
import { LuChevronDown, LuChevronRight, LuTrash } from "react-icons/lu"; // Assuming these are correct icons
// Import ChatSession type
import { ChatSession } from '../chat-history'; // Adjust path if necessary

/**
 * Props for the ChatAccordion component.
 */
interface ChatAccordionProps {
    /** An array of chat sessions to display in the accordion history. */
    sessions: ChatSession[];
    /** Callback triggered when a session item is clicked. */
    onOldChatSelected: (sessionId: string) => void;
    /** Callback triggered when the delete button for a session is clicked. */
    onDeleteChat: (sessionId: string) => void;
    /** Optional font family to use for labels. */
    fontFamily?: string;
    /** The ID of the currently active session, used for highlighting. */
    currentSessionId?: string | null;
    /** Whether the chat sessions are currently loading. */
    loading?: boolean;
    /** The current search query string. */
    searchQuery?: string;
    /** Whether there are more sessions to load. */
    hasMore?: boolean;
    /** Callback triggered when the show more button is clicked. */
    onLoadMore?: () => void;
}

/**
 * A collapsible accordion component for displaying and managing chat history.
 * Lists all previous chat sessions and provides options to select or delete them.
 * 
 * @param {ChatAccordionProps} props - The component props.
 * @returns {JSX.Element} The rendered accordion history.
 */
export const ChatAccordion = (props: ChatAccordionProps) => {

    // State to manage the open/closed state of the Accordion item(s)
    const [openItems, setOpenItems] = useState(['Chats']); // Keep 'Chats' open by default?
    const [hoveredSessionId, setHoveredSessionId] = useState<string | null>(null);

    /**
     * Updates the state of which accordion items are currently expanded.
     */
    const handleAccordionEvent = (e : AccordionValueChangeDetails) => {
        setOpenItems(e.value);
    }

    /**
     * Handles selecting a chat session from the list.
     */
    const handleChatSelect = (sessionId: string) => {
        props.onOldChatSelected(sessionId);
    }

    /**
     * Handles deleting a chat session, with a confirmation prompt.
     */
    const handleChatDelete = (event: React.MouseEvent, sessionId: string) => {
        event.stopPropagation(); // Prevent selection when clicking delete
        if (window.confirm("Are you sure you want to delete this chat?")) {
            props.onDeleteChat(sessionId);
        }
    }

    /**
     * Determines whether a session button should use the active (highlighted) variant.
     */
    const handleButtonVariant = (sessionId : string) => {
        return sessionId === props.currentSessionId;
    }

    return (
        <Accordion.Root size={'sm'} variant={'outline'} width={'100%'} collapsible defaultValue={['Chats']} onValueChange={handleAccordionEvent} display="flex" flexDir="column" height="100%">
            <Accordion.Item key={'chats-accordion-root'} value={"Chats"} display="flex" flexDir="column" flex="1" minH={0}>
                <Accordion.ItemTrigger>
                    <Span fontFamily={props.fontFamily} display={'flex'} gap={2} width={'100%'} p={'0.5rem 1rem 0.5rem .45rem'} borderRadius={'sm'} justifyContent={'flex-start'} alignItems={'center'} flex="1" color={'colorPalette.fg'} _hover={{ bg: 'colorPalette.subtle' }} >
                        <Box transition="transform 0.2s" transform={openItems.includes("Chats") ? "rotate(90deg)" : "rotate(0deg)"}>
                            <LuChevronRight />
                        </Box>
                        Chats {props.loading ? '' : `(${props.sessions.length ? props.sessions.length : '0'})`}
                    </Span>
                </Accordion.ItemTrigger>
                <Accordion.ItemContent overflowY="auto" flex="1" pr={1}>
                    {props.loading ? (
                        <Stack gap={4} p={4}>
                            <SkeletonText noOfLines={4} gap={4} />
                        </Stack>
                    ) : props.sessions && props.sessions.length > 0 ? (
                        <>
                            {props.sessions.map((session) => (
                                <Accordion.ItemBody
                                    key={session.sessionId}
                                    id={session.sessionId}
                                    p={0}
                                    w={'100%'}
                                    display={'flex'}
                                >
                                    <Button
                                        display={'flex'}
                                        flex={1}
                                        pl={'1.75rem'}
                                        justifyContent={'space-between'}
                                        variant={handleButtonVariant(session.sessionId) ? 'subtle' : 'ghost'}
                                        size="sm"
                                        onClick={() => handleChatSelect(session.sessionId)}
                                        onMouseEnter={() => setHoveredSessionId(session.sessionId)}
                                        onMouseLeave={() => setHoveredSessionId(null)}
                                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleChatSelect(session.sessionId); } }}
                                        as={'div'}
                                        tabIndex={0}
                                        role="button"
                                    >
                                        <Box overflow="hidden" flex={1} minW={0} textAlign="left">
                                            <Text textOverflow={'ellipsis'} overflow="hidden" fontSize="sm" fontFamily={props.fontFamily} whiteSpace="nowrap">{session.summary || `Chat ${new Date(session.created).toLocaleDateString()}`}</Text>
                                            {session.snippet && (
                                                <Text textOverflow={'ellipsis'} overflow="hidden" fontSize="xs" color="fg.muted" fontFamily={props.fontFamily} whiteSpace="nowrap">{session.snippet}</Text>
                                            )}
                                        </Box>
                                        <IconButton
                                            aria-label={`Delete chat ${session.summary || ''}`}
                                            size="sm"
                                            variant="ghost"
                                            color="fg.muted"
                                            _hover={{ color: 'red.500' }}
                                            onClick={(e) => handleChatDelete(e, session.sessionId)}
                                            ml={2}
                                            flexShrink={0}
                                            opacity={hoveredSessionId === session.sessionId ? 1 : 0}
                                        >
                                            <LuTrash />
                                        </IconButton>
                                    </Button>
                                </Accordion.ItemBody>
                            ))}
                            {props.hasMore && (
                                <Box px={4} py={2}>
                                    <Button
                                        variant="ghost"
                                        size="xs"
                                        width="100%"
                                        fontFamily={props.fontFamily}
                                        color="fg.muted"
                                        onClick={props.onLoadMore}
                                    >
                                        Show more
                                    </Button>
                                </Box>
                            )}
                        </>
                    ) : (
                        <Box pl={'1.75rem'} py={2}>
                            <Text fontSize="sm" color="fg.muted" fontFamily={props.fontFamily}>
                                {props.searchQuery ? 'No matches found.' : 'No saved chats yet.'}
                            </Text>
                        </Box>
                    )}
                </Accordion.ItemContent>
            </Accordion.Item>
        </Accordion.Root>
    )
}
